import React, { useMemo, useState } from 'react';
import { BookOpen, MessageCircle, RotateCcw } from 'lucide-react';
import MarkdownText from '../../../components/MarkdownText';
import { getLanguage, t } from '../../../i18n';
import { CARD_DRAFT_TYPES } from '../composer/cardTemplates';

function getReadingSegments(card) {
  if (Array.isArray(card?.readingSegments)) return card.readingSegments;
  return [];
}

function ArticlePreview({ card }) {
  const segments = getReadingSegments(card).filter((segment) => segment.type !== 'scene');
  return (
    <div className="composer-reading-preview">
      <div className="composer-reading-preview-heading">
        <BookOpen size={17} aria-hidden="true" />
        <h3><MarkdownText text={card.readingTitle || card.front || ''} /></h3>
      </div>
      <div className="composer-reading-preview-body">
        {segments.length > 0 ? segments.map((segment, index) => (
          <div key={`${segment.type}-${index}`} className="composer-article-paragraph">
            <MarkdownText text={segment.text || ''} />
          </div>
        )) : <MarkdownText text={card.readingContent || card.back || ''} />}
      </div>
    </div>
  );
}

function DialogPreview({ card }) {
  const names = card.readingCharacterNames || { A: 'A', B: 'B' };
  const segments = getReadingSegments(card);
  return (
    <div className="composer-dialog-preview">
      <div className="composer-reading-preview-heading">
        <MessageCircle size={17} aria-hidden="true" />
        <h3><MarkdownText text={card.readingTitle || card.front || ''} /></h3>
      </div>
      <div className="composer-dialog-content">
        {segments.map((segment, index) => {
          if (segment.type === 'scene') {
            return <div key={`scene-${index}`} className="composer-dialog-scene"><MarkdownText text={segment.text} /></div>;
          }
          if (segment.type === 'story') {
            return <div key={`story-${index}`} className="composer-dialog-story"><MarkdownText text={segment.text} /></div>;
          }
          const speaker = segment.speaker === 'B' ? 'B' : 'A';
          const lines = String(segment.text || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
          const isPractice = lines.length >= 2;
          return (
            <div key={`${speaker}-${index}`} className={`composer-dialog-row speaker-${speaker.toLowerCase()} ${isPractice ? 'practice' : 'display-only'}`}>
              <div className="composer-dialog-bubble">
                <span className="composer-dialog-speaker">{names[speaker] || speaker}</span>
                <MarkdownText text={lines[0] || ''} />
                {isPractice && (
                  <div className="composer-dialog-answer"><MarkdownText text={lines.slice(1).join('\n')} /></div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QaPreview({ card, compact }) {
  const language = getLanguage();
  const [flipped, setFlipped] = useState(false);
  const visibleText = flipped ? card.back : card.front;
  return (
    <button
      type="button"
      className={`composer-qa-preview ${flipped ? 'flipped' : ''} ${compact ? 'compact' : ''}`}
      onClick={() => setFlipped((value) => !value)}
      aria-label={flipped ? t('noteComposer.preview.showQuestion', language) : t('noteComposer.preview.showAnswer', language)}
    >
      <span className="composer-preview-side">{flipped ? 'A' : 'Q'}</span>
      <span className="composer-qa-text"><MarkdownText text={visibleText || ''} /></span>
      {(flipped ? card.backMemo : card.memo) && (
        <span className="composer-qa-memo"><MarkdownText text={flipped ? card.backMemo : card.memo} /></span>
      )}
      <RotateCcw className="composer-preview-flip-icon" size={16} aria-hidden="true" />
    </button>
  );
}

function CardDraftPreview({ card, compact = false }) {
  const type = useMemo(() => card?.draftType || (
    card?.cardType === 'reading' ? CARD_DRAFT_TYPES.ARTICLE : card?.cardType
  ), [card]);
  if (!card) return null;
  if (type === CARD_DRAFT_TYPES.DIALOG) return <DialogPreview card={card} />;
  if (type === CARD_DRAFT_TYPES.ARTICLE) return <ArticlePreview card={card} />;
  return <QaPreview card={card} compact={compact} />;
}

export default CardDraftPreview;
